import { Stack, Typography } from '@mui/material';

import { useChartColorDefinitions } from '@/components/charts/constants';
import { LegendItem } from '@/components/charts/Legend/Legend';
import { ColorBox } from '@/components/display/ColorBox/ColorBox';
import { DiagonalSplitColorBox } from '@/components/display/ColorBox/DiagonalSplitColorBox';
import { OversizedMetricItem } from '@/components/display/OversizedMetricItem/OversizedMetricItem';
import { Divider } from '@/components/Divider';
import { Button } from '@/components/form/baseInputs/Button';
import { EMPTY_CONTENT_HYPHEN } from '@/components/typography/placeholders';
import { useModalState } from '@/hooks/useModalState';
import { useCanEditHouseholdData } from '@/modules/authentication/hooks/useCanEditHouseholdData';
import { CreateEntityShortFormModal } from '@/modules/entities/EntityShortFormModal/CreateEntityShortFormModal';
import { ROUTE_KEYS } from '@/navigation/constants';
import { getCompletePathFromRouteKey } from '@/navigation/navigationUtils';
import { COLORS } from '@/styles/tokens/colors';
import { formatCurrency } from '@/utils/formatting/currency';

import { useHouseholdEstateValueMetrics } from './hooks/useHouseholdEstateValueMetrics';
import type { HouseholdEstateValueMetrics } from './HouseholdEstateValueMetrics';
import {
  COMPACT_CURRENCY_OPTIONS,
  EstateValuePieChart,
  formatCompactCurrency,
  formatLiquidityLabel,
  HouseholdAssetsSummary,
} from './HouseholdEstateValueMetrics.utils';
import { NetWorthInsuranceTooltip } from './NetWorthInsuranceTooltip';

export interface HouseholdEstateValueMetricsProps {
  metrics?: HouseholdEstateValueMetrics | null;
  assetsSummary?: HouseholdAssetsSummary | null;
  householdId: string;
  hasEntities: boolean;
}

export function HouseholdEstateValueMetricsPortrait({
  metrics,
  assetsSummary,
  householdId,
  hasEntities,
}: HouseholdEstateValueMetricsProps) {
  const colors = useChartColorDefinitions();
  const [{ isModalOpen }, { openModal, closeModal }] = useModalState();
  const canEditHousehold = useCanEditHouseholdData();

  const { pieChartSeriesData, onePercentOfSum } =
    useHouseholdEstateValueMetrics({
      metrics,
    });

  return (
    <>
      {isModalOpen && (
        <CreateEntityShortFormModal
          onClose={closeModal}
          isOpen={isModalOpen}
          householdId={householdId}
        />
      )}

      <Stack px={2} spacing={2} height="100%">
        <OversizedMetricItem
          title="Total net worth"
          tooltip={<NetWorthInsuranceTooltip />}
          titleVariant="title-s"
          valueVariant="title-xl"
          valueColor={COLORS.TEXT.PRIMARY}
          sx={{ gap: 0.25 }}
          value={
            metrics
              ? formatCurrency(
                  metrics.totalEstateValue,
                  COMPACT_CURRENCY_OPTIONS
                )
              : EMPTY_CONTENT_HYPHEN
          }
          valueLabel={
            assetsSummary
              ? formatLiquidityLabel(
                  assetsSummary.liquidValue,
                  assetsSummary.liquidPercentage
                )
              : undefined
          }
          valueLabelColor={COLORS.TEXT.PRIMARY}
        />
        <Stack justifyContent="center" alignItems="center" direction="row">
          <EstateValuePieChart
            pieChartSeriesData={pieChartSeriesData}
            onePercentOfSum={onePercentOfSum}
            size={160}
          />
        </Stack>

        <Stack
          spacing={2}
          divider={<Divider sx={{ borderColor: COLORS.NAVY[100] }} />}
        >
          <Stack spacing={1.5} alignItems="flex-start" direction="row">
            <Stack pt="2px">
              <ColorBox color={colors.PRIMARY.backgroundColor} width={16} />
            </Stack>
            <OversizedMetricItem
              title="In estate"
              titleVariant="title-s"
              valueVariant="title-xl"
              valueColor={COLORS.TEXT.PRIMARY}
              sx={{ gap: 0.25 }}
              value={
                metrics
                  ? formatCurrency(
                      metrics.inEstateValue,
                      COMPACT_CURRENCY_OPTIONS
                    )
                  : EMPTY_CONTENT_HYPHEN
              }
              valueLabel={
                assetsSummary
                  ? formatLiquidityLabel(
                      assetsSummary.inEstateLiquidValue,
                      assetsSummary.inEstateLiquidPercentage
                    )
                  : undefined
              }
              valueLabelColor={COLORS.TEXT.PRIMARY}
              valueLabelSpacing={1}
            />
          </Stack>
          <Stack spacing={1}>
            <Stack spacing={1.5} alignItems="flex-start" direction="row">
              <Stack pt="2px">
                <DiagonalSplitColorBox
                  topLeftColor={colors.SECONDARY.backgroundColor}
                  bottomRightColor={colors.TERTIARY.backgroundColor}
                  width={16}
                />
              </Stack>
              <OversizedMetricItem
                title="Out of estate"
                titleVariant="title-s"
                valueVariant="title-xl"
                valueColor={COLORS.TEXT.PRIMARY}
                sx={{ gap: 0.25 }}
                value={
                  metrics
                    ? formatCurrency(
                        metrics.outOfEstateValue,
                        COMPACT_CURRENCY_OPTIONS
                      )
                    : EMPTY_CONTENT_HYPHEN
                }
                valueLabel={
                  assetsSummary
                    ? formatLiquidityLabel(
                        assetsSummary.outOfEstateLiquidValue,
                        assetsSummary.outOfEstateLiquidPercentage
                      )
                    : undefined
                }
                valueLabelColor={COLORS.TEXT.PRIMARY}
                valueLabelSpacing={1}
              />
            </Stack>

            <Stack direction="row" spacing={2} pl="32px">
              <LegendItem
                primaryText={
                  <Typography variant="title-xs" component="span">
                    {formatCompactCurrency(metrics?.outOfEstateFamilyValue)}
                  </Typography>
                }
                secondaryText={
                  <Typography variant="body-xs" component="span">
                    Family giving
                  </Typography>
                }
                legendItemColor={colors.SECONDARY.backgroundColor}
                containerSx={{ gap: 0.25 }}
              />

              <LegendItem
                primaryText={
                  <Typography variant="title-xs" component="span">
                    {formatCompactCurrency(metrics?.outOfEstateCharityValue)}
                  </Typography>
                }
                secondaryText={
                  <Typography variant="body-xs" component="span">
                    Charitable giving
                  </Typography>
                }
                legendItemColor={colors.TERTIARY.backgroundColor}
                containerSx={{ gap: 0.25 }}
              />
            </Stack>
          </Stack>
        </Stack>
        {!hasEntities && canEditHousehold && (
          <Stack direction="row" justifyContent="end">
            <Button variant="primary" size="sm" fullWidth onClick={openModal}>
              Add an entity to get started
            </Button>
          </Stack>
        )}
        <Stack direction="row" justifyContent="end">
          <Button
            variant="secondary"
            size="sm"
            href={getCompletePathFromRouteKey(
              ROUTE_KEYS.HOUSEHOLD_ESTATE_BALANCE_SHEET,
              { householdId }
            )}
          >
            View balance sheet
          </Button>
        </Stack>
      </Stack>
    </>
  );
}
